// The Leadership next step section (GRO-238): a founder's own next step on the deal, kept in its
// own Salesforce field, apart from the rep's. Everyone sees it; leadership and admin get an editor
// whose Save writes to Salesforce through `POST /api/pipeline/{opp_id}/leadership-next-step`.
// It sits in the drawer and, as the `card` variant, at the top of the table's Next step hover card.
//
// Whether to offer the editor comes from `GET /api/me`, asked once per page load. The server
// checks the role again on every save; hiding the editor is only a courtesy.

import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { LeadershipNextStep, Opportunity, Viewer } from '../api/types.ts';
import {
  AccessDeniedError, ApiError, SessionExpiredError, StillBuildingError, fetchViewer, saveLeadershipNextStep,
} from '../api/client.ts';
import type { TokenGetter } from '../api/client.ts';
import {
  MAX_LEADERSHIP_NEXT_STEP_LENGTH, leadershipByline, normaliseLeadershipText, saveErrorMessage,
} from '../board/leadershipNextStep.ts';
import '../styles/leadership-next-step.css';

// One `/api/me` per page load, shared by every drawer. A failure is forgotten so the next drawer asks again.
let viewerRequest: Promise<Viewer> | null = null;

function useViewer(getToken: TokenGetter): Viewer | null {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  useEffect(() => {
    let current = true;
    viewerRequest ??= fetchViewer(getToken);
    viewerRequest
      .then(loaded => { if (current) setViewer(loaded); })
      .catch(() => { viewerRequest = null; });
    return () => { current = false; };
  }, [getToken]);
  return viewer;
}

// The refusal's code when the server sent one. `request` turns a lapsed session, a 403 and a 503
// `building` into their own error classes, which carry no code, so each maps to its status here.
function messageFor(error: unknown): string {
  if (error instanceof SessionExpiredError) return saveErrorMessage(401, null);
  if (error instanceof AccessDeniedError) return saveErrorMessage(403, 'no_access');
  if (error instanceof StillBuildingError) return saveErrorMessage(503, 'building');
  if (error instanceof ApiError) return saveErrorMessage(error.status, error.code);
  return saveErrorMessage(null, null);
}

export function LeadershipNextStepSection({
  opportunity, today, getToken, onSaved, variant = 'drawer',
}: {
  opportunity: Opportunity;
  today: string;
  getToken: TokenGetter;
  // Hands a save to the board, which lays it over the payload for the table and the drawer.
  onSaved: (oppId: string, value: LeadershipNextStep | null) => void;
  // The hover card heads it like the card's other sections, and leaves it out when there is
  // neither a step to show nor an editor to offer.
  variant?: 'drawer' | 'card';
}) {
  const oppId = opportunity.opp_id;
  const viewer = useViewer(getToken);
  const canEdit = viewer?.can_edit_leadership_next_step ?? false;

  // The board has already laid any save since load over the payload. The drawer mounts this keyed
  // on the opportunity, so switching rows starts the editor over.
  const current = opportunity.leadership_next_step;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // What Save would send, and whether it would change anything.
  const normalisedDraft = normaliseLeadershipText(draft);
  const tooLong = normalisedDraft.length > MAX_LEADERSHIP_NEXT_STEP_LENGTH;
  const unchanged = normalisedDraft === (current?.text ?? '');

  // Focus goes back to Edit (or Add) when the editor closes, rather than nowhere: the textarea it
  // was in is gone. The hover card reads that move to know the editor is done.
  const editButton = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  useEffect(() => {
    if (wasEditing.current && !editing) editButton.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  function startEditing() {
    setDraft(current?.text ?? '');
    setError(null);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setError(null);
  }

  function save() {
    if (saving || tooLong || unchanged) return;
    setSaving(true);
    setError(null);
    saveLeadershipNextStep(getToken, oppId, normalisedDraft)
      .then(response => {
        onSaved(oppId, response.leadership_next_step);
        setEditing(false);
      })
      .catch((failure: unknown) => setError(messageFor(failure)))
      .finally(() => setSaving(false));
  }

  // Enter saves, since the field is one line in Salesforce. Escape leaves the editor, not the drawer.
  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      save();
    }
    if (event.key === 'Escape') {
      event.stopPropagation();
      cancelEditing();
    }
  }

  const byline = current ? leadershipByline(current, today) : '';
  const inCard = variant === 'card';
  if (inCard && !current && !canEdit) return null;

  return (
    <section className={'leadership-step' + (inCard ? ' leadership-step-card' : '')}>
      <div className="leadership-step-head">
        {inCard ? <span className="popover-head">Leadership next step</span> : <h3>Leadership next step</h3>}
        {canEdit && !editing ? (
          <button ref={editButton} type="button" className="link-button" onClick={startEditing}>{current ? 'Edit' : 'Add'}</button>
        ) : null}
      </div>

      {/* The saved value, or the editor in its place. */}
      {!editing ? (
        current ? (
          <>
            <p>{current.text}</p>
            {byline ? <p className="muted">{byline}</p> : null}
          </>
        ) : (
          <p className="muted">None yet.</p>
        )
      ) : (
        <div className="leadership-step-editor">
          <textarea
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            rows={3}
            autoFocus
            disabled={saving}
            aria-label="Leadership next step"
            placeholder="What should happen next on this deal"
          />
          <div className="leadership-step-actions">
            <span className={tooLong ? 'level-bad' : 'muted'}>
              {normalisedDraft.length}/{MAX_LEADERSHIP_NEXT_STEP_LENGTH}
              {normalisedDraft === '' && current ? ' · saving it empty clears it' : ''}
            </span>
            <button type="button" className="button" onClick={cancelEditing} disabled={saving}>Cancel</button>
            <button type="button" className="button button-primary" onClick={save} disabled={saving || tooLong || unchanged}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="muted leadership-step-note">Saved to Salesforce as the deal's leadership next step.</p>
        </div>
      )}

      {error ? <p className="notice notice-error" role="alert">{error}</p> : null}
    </section>
  );
}
